/**
 * SelectionPanel.jsx — PR ⑬d
 *
 * Why `auto` picked what it picked.
 *
 * `auto` nests the sheet once per candidate and returns the one that MEASURED
 * better — it is not a heuristic, so the candidates table is real evidence and
 * worth showing. Two contract rules shape this component (§8):
 *
 *   - `reason` is written to be displayed VERBATIM. Not a code to map, not a
 *     sentence to rewrite.
 *   - `candidates[]` arrives in EVALUATION order, not ranked, and rows are
 *     matched by `key`. Position carries no meaning, so nothing here indexes.
 *
 * The cartons column is labelled "job cartons" when `scope == "job"`: that
 * figure counts across the whole job, and a plain "cartons" header would read
 * as a per-sheet number.
 */
import React from 'react';
import { useTheme } from './theme';
import { selectionSummary } from './prepress';

const NUM = (v) => (Number.isFinite(v) ? String(Math.round(v * 100) / 100) : '—');

export default function SelectionPanel({ result }) {
  const { P, MO, FN } = useTheme();
  const sel = selectionSummary(result);
  if (!sel) return null;

  // Rev 3 §8.5 pinned exactly five fields, always present. `sheets`,
  // `utilization` and `score` do NOT exist — utilization is omitted
  // deliberately, being gross and tile-inflated, so it is the wrong number to
  // show beside a pairing decision.
  const cols = [
    { key: 'cartons', label: sel.cartonsLabel },
    { key: 'pieces_placed', label: 'pieces' },
    { key: 'eval_ms', label: 'eval ms' },
  ];

  const th = {
    fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase',
    letterSpacing: 0.4, textAlign: 'right', padding: '4px 8px', whiteSpace: 'nowrap',
  };
  const td = { fontFamily: MO, fontSize: 11, textAlign: 'right', padding: '5px 8px' };

  return (
    <div style={{
      padding: 14, borderRadius: 8, background: P.sheet,
      border: `0.5px solid ${P.ruleSoft}`, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Automatic selection
      </div>

      {/* Verbatim — the backend writes this to be read as-is. */}
      {sel.reason && (
        <div style={{ fontFamily: FN, fontSize: 12.5, color: P.ink, lineHeight: 1.5 }}>
          {sel.reason}
        </div>
      )}

      {sel.candidates.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>candidate</th>
                {cols.map((c) => <th key={c.key} style={th}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {sel.candidates.map((c) => {
                const won = c.key === sel.winner;
                return (
                  <tr key={c.key} style={{
                    background: won ? P.okSoft : 'transparent',
                    borderTop: `0.5px solid ${P.ruleSoft}`,
                  }}>
                    <td style={{ ...td, textAlign: 'left', color: P.ink, fontWeight: won ? 600 : 400 }}>
                      {c.key}{won && <span style={{ color: P.ok, marginLeft: 6 }}>✓ chosen</span>}
                      {c.audits_ok === false && (
                        <span title="This candidate failed its audit" style={{ color: P.warn, marginLeft: 6 }}>⚠</span>
                      )}
                    </td>
                    {cols.map((col) => (
                      <td key={col.key} style={{ ...td, color: won ? P.ink : P.muted }}>
                        {NUM(c[col.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, lineHeight: 1.45 }}>
        Each candidate was nested for real on this sheet, not estimated. Cartons are
        tile-aware — a paired tile counts two. Utilization is deliberately absent: it is
        gross and tile-inflated, so it is the wrong number to weigh a pairing decision on.
      </div>
    </div>
  );
}
